import _ from "lodash";
import React from "react";
import { useThunkDispatch } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";

type NavFiltersProps = {
    children?: React.ReactElement<any>[],
};

function getAllParameterNames(children: React.ReactElement<any>[]): string[] {
    function* _getAllParameterNames(element: React.ReactElement<any>): Generator<string, any, string> {
        if (!element) return;

        const name = element.props?.["name"];
        if (name) {
            yield name;
        }

        let children = element.props?.["children"] ?? [];
        if (children && !_.isArray(children)) {
            children = [children];
        }

        for (let child of children) {
            yield* _getAllParameterNames(child);
        }
    }

    return _.uniq(children?.flatMap(c => Array.from(_getAllParameterNames(c))) ?? []);
}

const NavFiltersParameterName = "__nav_filters_linked_parameter_names";

function NavFilters(props: NavFiltersProps) {
    const { children } = props;
    const dispatch = useThunkDispatch();

    const allParameterNames = getAllParameterNames(children);

    React.useEffect(() => {
        dispatch(updateParameterValue(NavFiltersParameterName, allParameterNames));
    }, [allParameterNames]);

    return (
        <>
            {children}
        </>
    );
}

(NavFilters as DocumentedComponent).metadata = {
    description: "The `NavFilters` component serves as a container for any number of input components.  This is a transient container component - it is not rendered on the front-end at all and, as such, has no associated styling or layout.  Its purpose is purely to collect the `name` values for all contained inputs, in order to facilitate the automatic persistence/serialization of the associated parameter values when the `navigateToPage` canvas function is invoked.",
    attributes: [
        { name: "children", type: "object", exactType: "React.ReactNode", description: "The input components to automatically pass along to the target when navigation occurs via a `navigateToPage` function call.  Can be nested arbitrarily deep within any other containers/components.  If a parameter value is manually being manipulated by one of the children (via an unwrapped/uncontrolled component), a `name` prop with the associated parameter name can simply be added to any of the associated components." }
    ]
};

export { NavFilters, NavFiltersParameterName };